import React from 'react'
import { useManualQuery, useSubscription } from 'graphql-hooks'
import { useIntl } from 'react-intl'
import isNumber from 'lodash/isNumber'
import { App } from 'antd'

import * as logger from '../../services/logger'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice, setAuthCompanyPlan, setAuthCompany } from '../../store/auth-company-slice'
import { selectAuthUserSlice, setAuthUserLocationWorkWeek, setAuthUserWorkWeek, setAuthUserProfile } from '../../store/auth-user-slice'
import { setLeaveRequestActionEvent } from '../../store/leave-request-action-event-slice'
import { setCompanySettingsBillingUpdatedActionEvent } from '../../store/company-settings-billing-updated-action-event-slice'
import { markOnboardingCompleteForFreemium } from '../../util/onboarding-v2-storage'
import {
  companyUpdateSubscription,
  getCompanyBillingInfo,
  getConnectedCalendars,
  getLeaveRequestByIdForSubscription,
  getToilRequestByIdForSubscription,
  getUserForLeaveRequestUpdateNotification
} from '../../graphql/custom-queries'

import { ISubscriptionEvent } from '@vacationtracker/shared/types/notification'
import { IGetConnectedCalendarsResponse, ILeaveRequestUpdate, IToilRequestUpdate, IUserForLeaveRequestUpdate } from '../../types/custom-queries'
import { selectLocaleSlice } from '../../store/locale-slice'
import { BillingTypesEnum } from '@vacationtracker/shared/types/billing'
import { IGetCompanyBillingInfoData } from '../../types/company'
import { updateLeaveRequestNotification } from '../../components/update-leave-request-notification'
import { updateToilRequestNotification } from '../../components/update-toil-request-notification'
import { setToilRequestActionEvent } from '../../store/toil-request-action-event-slice'
import { ApproversLevelEnum } from '@vacationtracker/shared/types/team'
import { apiCache } from '../../util/cache-api-response'

type CompanyEventResponse = {
  companyUpdates: ISubscriptionEvent
}

const CompanyUpdatesNotifications = () => {
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { authUser} = useAppSelector(selectAuthUserSlice)
  const { locale} = useAppSelector(selectLocaleSlice)
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const { notification } = App.useApp()

  const [getCompanyBillingInfoQuery] = useManualQuery<IGetCompanyBillingInfoData, { companyId: string }>(getCompanyBillingInfo)
  const [leaveRequestResponseQuery] = useManualQuery<ILeaveRequestUpdate, { leaveRequestId: string }>(getLeaveRequestByIdForSubscription)
  const [getUserForLeaveRequestUpdateNotificationQuery] = useManualQuery<IUserForLeaveRequestUpdate, { id: string }>(getUserForLeaveRequestUpdateNotification)
  const [getConnectedCalendarsQuery] = useManualQuery<IGetConnectedCalendarsResponse, { userId: string }>(getConnectedCalendars)
  const [getToilRequestByIdForSubscriptionQuery] = useManualQuery<IToilRequestUpdate, { toilRequestId: string }>(getToilRequestByIdForSubscription)

  useSubscription<CompanyEventResponse>({
    query: companyUpdateSubscription,
    variables: { companyId: authCompany?.id },
  }, ({ data, errors }) => {
    // The "errors" type is `object[] | undefined`, but WebSocket error returns just one object with `type: 'error'`
    const err: any = errors
    if (err && (err.type === 'error' || !Array.isArray(err))) {
      // Handled in the <Notifications /> component
      return
    } else if (errors || !data) {
      logger.error(...errors || [])
      return
    }
    try {
      const subscriptionEvent = JSON.parse(data.companyUpdates.originalEvent)
      const rawResult = data?.companyUpdates?.result

      // For USER_UPDATED events, we don't need a result object
      if (subscriptionEvent.eventType === 'USER_UPDATED') {
        handleSubscriptionEvent(subscriptionEvent, null)
        return
      }

      if (!rawResult) {
        // Nothing to do here, just return
        return
      }
      const result = JSON.parse(rawResult)
      handleSubscriptionEvent(subscriptionEvent, result)
    } catch (err) {
      logger.error('Error parsing subscription event (CompanyUpdatesNotifications)', err)
    }
  })

  const handleSubscriptionEvent = async (event, result) => {
    if (!authCompany || !authUser.id) {
      return
    }

    switch (event.eventType) {
      case 'SUBSCRIPTION_CHANGED':
        dispatch(setAuthCompanyPlan(event.plan))

        if (event.plan === 'Freemium' && authUser?.id && authCompany?.id) {
          markOnboardingCompleteForFreemium(authUser.id, authCompany.id)
        }

        if (event.paymentProcessor === 'microsoft-billing') {
          dispatch(setAuthCompany({
            ...authCompany,
            billing: {
              ...authCompany?.billing,
              quantity: event.quantity,
              subscriptionPeriod: event.subscriptionPeriod,
              msSubscriptionStatus: event.msSubscriptionStatus,
              msPeriodStartDate: event.msPeriodStartDate,
              msPeriodEndDate: event.msPeriodEndDate,
            },
          }))
        }
        break
      case 'PAYMENT_PROCESSOR_UPDATED':
        if (event.paymentProcessor === 'stripe'
          && authCompany.billing?.billingType === BillingTypesEnum.seatBased
          && isNumber(authCompany.billing?.seats)
        ) {
          const billingResponse = await getCompanyBillingInfoQuery({ variables: { companyId: authCompany.id }})
          if (!billingResponse.data || billingResponse.error) throw billingResponse.error

          dispatch(setAuthCompany({
            ...authCompany,
            billing: {
              ...authCompany?.billing,
              seats: billingResponse.data.getCompany.billing.seats,
            },
          }))
          dispatch(setCompanySettingsBillingUpdatedActionEvent(event))
        }
        break
      case 'BILLING_UPDATED':
        try {
          const billingResponse = await getCompanyBillingInfoQuery({ variables: { companyId: authCompany.id }})
          if (billingResponse.data && !billingResponse.error) {
            dispatch(setAuthCompany({
              ...authCompany,
              billing: billingResponse.data.getCompany.billing as any,
            }))
          }
        } catch (error) {
          logger.info('Failed to fetch updated billing information:', error)
        }
        break
      case 'LOCATION_WORK_WEEK_UPDATED':
        dispatch(setAuthUserLocationWorkWeek(event))
        break
      case 'USER_WORK_WEEK_UPDATED':
        dispatch(setAuthUserWorkWeek(event))
        break
      case 'USER_UPDATED':
        if (event.userId === authUser.id) {
          const profileUpdate: { name?: string; imageUrl?: string } = {}
          if (event.name !== undefined) {
            profileUpdate.name = event.name
          }
          if (event.imageUrl !== undefined) {
            profileUpdate.imageUrl = event.imageUrl
          }
          if (Object.keys(profileUpdate).length > 0) {
            dispatch(setAuthUserProfile(profileUpdate))
          }
        }
        break
      case 'LEAVE_REQUEST_UPDATED':
      case 'LEAVE_REQUEST_CREATED':
      case 'LEAVE_REQUEST_CANCELLED':
      case 'LEAVE_REQUEST_APPROVED':
      case 'LEAVE_REQUEST_DENIED':
      case 'LEAVE_REQUEST_EXPIRED':
      case 'LEAVE_REQUEST_ADDED':
      case 'LEAVE_REQUEST_DELETED':
      case 'LEAVE_REQUEST_STATUS_CHANGED':

        if (event.isMigration) return
        // Clear cache to ensure fresh data is fetched
        apiCache.clear()
        if (result?.eventAction === 'LeaveRequestAction') {
          if (!result.id) {
            logger.warning('LEAVE REQUEST ID NOT FOUND', result)
            return
          }
          let isCalendarConnected = false
          try {
            const leaveRequestResponse = await leaveRequestResponseQuery({ variables: { leaveRequestId: result.id }})
            if (!leaveRequestResponse.data || leaveRequestResponse.error) throw leaveRequestResponse.error
            const getLeaveRequestResponse = leaveRequestResponse.data.getLeaveRequest

            const hasMultipleApprovers = Boolean(
              getLeaveRequestResponse.user.team.approvers.find(approver => approver.approverLevels.includes(ApproversLevelEnum.SECOND))
            )

            let creator = event.creator

            if (hasMultipleApprovers && getLeaveRequestResponse.status !== 'APPROVED') {
              const approverForLeaveRequest = getLeaveRequestResponse.user.team.approvers.find(approver => approver.id === authUser.id)
              // if the leave request is not approved for the first level, and the user is an approver for the second level, then return
              if (
                !getLeaveRequestResponse?.approvedLevel &&
                approverForLeaveRequest?.approverLevels.includes(ApproversLevelEnum.SECOND) &&
                !approverForLeaveRequest?.approverLevels.includes(ApproversLevelEnum.FIRST)) {
                if (!event.isOnboarding) {
                  dispatch(setLeaveRequestActionEvent(event))
                }
                return
              }


              // If the leave request is open and the approved level is first and the user is an approver for the first level, then dispatch the event and return
              // TODO: Need to update this to send notification to the other approvers that the leave request is approved by the first level approver
              if (
                getLeaveRequestResponse.status === 'OPEN' &&
                getLeaveRequestResponse?.approvedLevel === ApproversLevelEnum.FIRST &&
                approverForLeaveRequest?.approverLevels.includes(ApproversLevelEnum.FIRST)
              ) {
                if (!event.isOnboarding) {
                  dispatch(setLeaveRequestActionEvent(event))
                }
                return
              }

              // If the leave request is open and the approved level is first and the user is an approver for the second level,
              // then send notification to the second level approvers
              // we need to change creator because this is fire on event LEAVE_REQUEST_STATUS_CHANGED where is creator is the approver of the first level
              // we need creator to be user who is the creator of the leave request
              if (
                getLeaveRequestResponse.status === 'OPEN' &&
                getLeaveRequestResponse?.approvedLevel === ApproversLevelEnum.FIRST &&
                approverForLeaveRequest?.approverLevels.includes(ApproversLevelEnum.SECOND) &&
                event.eventType === 'LEAVE_REQUEST_STATUS_CHANGED'
              ) {
                creator = event.userId
              }

              // Return if the user is the creator and the leave is update by event LEAVE_REQUEST_STATUS_CHANGED
              if (event.eventType === 'LEAVE_REQUEST_STATUS_CHANGED' && authUser.id === getLeaveRequestResponse.user.id) {
                if (!event.isOnboarding) {
                  dispatch(setLeaveRequestActionEvent(event))
                }
                return
              }
            }

            let userResponse
            if (authUser.role === 'Admin') {
              userResponse = await getUserForLeaveRequestUpdateNotificationQuery({ variables: {
                id: creator,
              }})
            } else if (authUser.approverTo.find(u => u.id === leaveRequestResponse.data?.getLeaveRequest.user.id)) {
              userResponse = await getUserForLeaveRequestUpdateNotificationQuery({ variables: {
                id: creator,
              }})
            } else if (authUser.id === getLeaveRequestResponse.user.id) {
              userResponse = await getUserForLeaveRequestUpdateNotificationQuery({ variables: {
                id: getLeaveRequestResponse.user.id,
              }})
            } else {
              return // regular users cant see other users profiles anyway
            }

            const calendarResponse = await getConnectedCalendarsQuery({ variables: { userId: authUser.id }})
            if (calendarResponse.data?.getConnectedGooogleCalendar?.calendarId || calendarResponse.data?.getConnectedOutlookCalendar?.calendarId) {
              isCalendarConnected = true
            }

            if (leaveRequestResponse.data && userResponse.data) {
              updateLeaveRequestNotification({
                subscriptionEvent: event,
                leaveRequest: {
                  getLeaveRequest: getLeaveRequestResponse,
                  getUser: userResponse.data.getUser,
                },
                result,
                authUser,
                authCompany,
                formatMessage,
                locale: locale.locale,
                isCalendarConnected,
                notification,
              })
            }
          } catch (err) {
            logger.error('ERROR FETCHING LEAVE REQUEST', err)
          }
          // Always dispatch the event to refresh dashboard data
          // The event contains isOnboarding flag if consumers need to handle it differently
          dispatch(setLeaveRequestActionEvent(event))
        }
        break
      case 'TOIL_REQUEST_UPDATED':
      case 'TOIL_REQUEST_CREATED':
      case 'TOIL_REQUEST_APPROVED':
      case 'TOIL_REQUEST_DENIED':
      case 'TOIL_REQUEST_EXPIRED':
      case 'TOIL_REQUEST_ADDED':
      case 'TOIL_REQUEST_DELETED':
      case 'TOIL_REQUEST_CANCELLED':
        if (event.isMigration) return
        if (result?.eventAction === 'ToilRequestAction') {
          try {
            const toilRequestResponse = await getToilRequestByIdForSubscriptionQuery({ variables: {
              toilRequestId: result.id,
            }})
            if (!toilRequestResponse.data || toilRequestResponse.error) throw toilRequestResponse.error
            let userResponse
            if (authUser.role === 'Admin') {
              userResponse = await getUserForLeaveRequestUpdateNotificationQuery({ variables: {
                id: event.creator,
              }})
            } else if (authUser.approverTo.find(u => u.id === toilRequestResponse.data?.getToilRequest.user.id)) {
              userResponse = await getUserForLeaveRequestUpdateNotificationQuery({ variables: {
                id: event.creator,
              }})
            } else {
              userResponse = await getUserForLeaveRequestUpdateNotificationQuery({ variables: {
                id: toilRequestResponse.data.getToilRequest.user.id,
              }})
            }
            if (toilRequestResponse.data && userResponse.data) {
              updateToilRequestNotification({
                subscriptionEvent: event,
                toilRequest: {
                  getToilRequest: toilRequestResponse.data.getToilRequest,
                  getUser: userResponse.data.getUser,
                },
                result,
                authUser,
                authCompany,
                formatMessage,
                locale: locale.locale,
                notification,
              })
            }
          } catch (err) {
            logger.error('ERROR FETCHING TOIL REQUEST', err)
          }
          // Don't dispatch the event if it's from onboarding to prevent modal closure
          if (!event.isOnboarding) {
            dispatch(setToilRequestActionEvent(event))
          }
        }
        break
      default:
        break
    }
  }

  return (<></>)
}

export default CompanyUpdatesNotifications
